import { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, Loader2, Building2 } from "lucide-react";

const DEBOUNCE_MS = 350;

async function fetchAddresses(text) {
  try {
    const res = await fetch(
      `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(text)}&limit=4&lat=43.674&lon=1.450`
    );
    const data = await res.json();
    return (data.features || []).map((f) => ({
      label: f.properties.label,
      city: f.properties.city,
      postcode: f.properties.postcode,
      type: "address",
    }));
  } catch {
    return [];
  }
}

async function fetchPlaces(text) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
      `q=${encodeURIComponent(text)}&format=json&limit=4&countrycodes=fr` +
      `&viewbox=1.2,43.8,1.7,43.5&bounded=1` +
      `&addressdetails=1&accept-language=fr`
    );
    const data = await res.json();
    return data
      .filter((r) => r.type !== "house" && r.type !== "street")
      .map((r) => ({
        label: r.display_name.split(",").slice(0, 3).join(","),
        fullLabel: r.display_name,
        city: r.address?.city || r.address?.town || r.address?.village || "",
        postcode: r.address?.postcode || "",
        type: "place",
        category: r.type,
      }));
  } catch {
    return [];
  }
}

export default function AddressAutocomplete({ value, onChange, placeholder, icon: Icon = MapPin, iconColor = "text-emerald-400", id, testId }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (text) => {
    if (text.length < 3) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const [addresses, places] = await Promise.all([
        fetchAddresses(text),
        fetchPlaces(text),
      ]);

      // Merge: places first (clinics, hospitals, etc.), then addresses
      const seen = new Set();
      const merged = [];

      for (const p of places) {
        const key = p.label.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(p);
        }
      }
      for (const a of addresses) {
        const key = a.label.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(a);
        }
      }

      setSuggestions(merged.slice(0, 6));
      setOpen(true);
      setActiveIndex(-1);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    onChange(text);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchSuggestions(text), DEBOUNCE_MS);
  };

  const handleSelect = (s) => {
    const selected = s.type === "place" ? s.fullLabel || s.label : s.label;
    setQuery(selected);
    onChange(selected);
    setOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          id={id}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="flex h-12 w-full rounded-lg border bg-secondary/50 border-white/10 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 pr-10"
          data-testid={testId}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading ? (
            <Loader2 className="h-4 w-4 text-zinc-500 animate-spin" />
          ) : (
            <Icon className={`h-4 w-4 ${iconColor} opacity-50`} />
          )}
        </div>
      </div>

      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-zinc-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(s)}
              className={`w-full text-left px-4 py-3 text-sm flex items-start gap-3 transition-colors ${
                i === activeIndex
                  ? "bg-amber-500/10 text-white"
                  : "text-zinc-300 hover:bg-white/5"
              }`}
              data-testid={`${testId}-suggestion-${i}`}
            >
              {s.type === "place" ? (
                <Building2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${i === activeIndex ? "text-amber-400" : "text-blue-400"}`} />
              ) : (
                <MapPin className={`h-4 w-4 mt-0.5 flex-shrink-0 ${i === activeIndex ? "text-amber-400" : "text-zinc-500"}`} />
              )}
              <div className="min-w-0">
                <span className="block truncate">{s.label}</span>
                <span className="text-xs text-zinc-500">
                  {s.type === "place" ? (
                    <span className="text-blue-400/70">Lieu &bull; </span>
                  ) : null}
                  {s.postcode} {s.city}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
